import { getAggregateError, } from "@bracket/catch";
import { totalActiveShares as totalActiveSharesV1, } from "@bracket/contractor-next/bracket-vault";
import { totalShares as totalSharesV2, } from "@bracket/contractor-next/bracket-vault-v2";
import type { ValidChainId, } from "@bracket/types";
import type { LoadedVault, } from "@types";
import { deepAssign, getVaultVersion, } from "@utils";
import { wagmiConfig as config, } from "@wagmiconfig";
import { cache, initialVaultCache, } from "./cache";
import { contract, multiCaller, } from "@bracket/chainography/contracts";

/**
 * Retrieves the total active shares for a vault with caching support.
 * The function handles both V1 and V2 vault versions, using different contract methods accordingly (totalShares for version 2, totalActiveSharesV1 for version 1).
 * 
 * @param {Object} params - The parameters object
 * @param {LoadedVault} params.vault - The vault to get total active shares for
 * @returns {Promise<bigint>} The total active shares value
 * @throws {Error} If the operation fails, throws an error with vault details
 */
export const totalActiveShares = async ({
  vault: {
		address,
		symbol,
		chain,
	},
}: {
	vault: LoadedVault;
}): Promise<bigint> => { 
	try {
		const now = Date.now();
		cache.vaults[address] = cache.vaults[address] || deepAssign({}, initialVaultCache);
		const { time, value, since, } = cache.vaults[address].total_active_shares;
		if(since > now - time) {
			return value;
		}
		const version = getVaultVersion(address);
		const contractName = version === "2" ? "BracketVaultV2" : "BracketVault";
		const functionName = version === "2" ? "totalShares" : "totalActiveShares";
		const [ [ next, ], error  ] = await multiCaller<[bigint]>(config, [
			contract(contractName, functionName, address),
		]);
		cache.vaults[address].total_active_shares.since = Date.now();
		cache.vaults[address].total_active_shares.value = next;
		return next;
	}
	catch(error) {
		throw getAggregateError(error, `failed getting total active shares for vault ${symbol} ${address}`);
	}
}
